import { withPlugins, inview } from '@weareduet/plugins';

// Matches the `lg` breakpoint (1024px) used by Tailwind and the responsive posters.
const mql = window.matchMedia('(min-width: 1024px)');

function isDesktop() {
	return mql.matches;
}

// Returns the URL to load, choosing mobile src on narrow viewports when available.
function resolveVideoSrc(video) {
	const { src: dataSrc, srcMobile } = video.dataset;
	if (srcMobile && !isDesktop()) return srcMobile;
	return dataSrc || video.src || null;
}

// Aspect ratio is NOT set here. It's owned by the `u-crop` utility, resolving
// `--crop` / `--crop-mobile` (set inline by videoAutoplay.twig) per breakpoint.
// Applying it from JS meant the card's height changed after first paint, and
// iOS Safari intermittently failed to re-centre the homepage hero's grid item
// against the new height — see custom-utilities.css.

// Returns the poster URL for the current viewport. The `poster` attribute takes
// a single URL — no srcset — so the desktop poster is what the markup ships
// (that's the one a crawler reads as the video's thumbnail) and the mobile one
// is swapped in here, alongside the src. The browser drops the poster for the
// first decoded frame on its own, so there's nothing to fade.
function resolvePosterSrc(video) {
	const { posterDesktop, posterMobile } = video.dataset;
	if (posterMobile && !isDesktop()) return posterMobile;
	return posterDesktop || null;
}

// Compared against the raw attribute, not `video.poster` — the property returns
// the URL resolved to an absolute one, which never equals the value we set.
function applyPoster(video) {
	const poster = resolvePosterSrc(video);
	if (poster && video.getAttribute('poster') !== poster) {
		video.setAttribute('poster', poster);
	}
}

function AutoplayVideos({ node, plugins: { inview } }) {
	inview.settings = {
		rootMargin: '0px 0px -100px 0px',
	};

	function initiatePlay(video) {
		if (video._isPlaying) return;

		const src = resolveVideoSrc(video);
		if (src && video.src !== src) {
			video.src = src;
			video.load();
		}

		// Request playback straight away rather than waiting for `canplay`:
		// play() is what actually starts the download in Safari. It won't buffer
		// a muted/inline video past metadata while the element has no `autoplay`
		// attribute, so `readyState` sits at 1 and `canplay` never fires — a
		// listener would wait forever. Chrome preloads to readyState 4 on its
		// own, which is why waiting only ever broke on iOS (see
		// textCarouselBlock.twig, whose slides carry `src` but no `autoplay`).
		//
		// Flagged as playing up front so a second `enter` can't stack another
		// play() on the pending one; cleared again if the request is refused
		// (iOS Low Power Mode) or interrupted by `exit`'s pause, so a later
		// enter retries.
		video._isPlaying = true;
		video
			.play()
			.then(() => {
				video.setAttribute('data-playing', 'true');
			})
			.catch(() => {
				video._isPlaying = false;
			});
	}

	const enter = ({ target }) => initiatePlay(target);
	const exit = ({ target }) => {
		if (target._isPlaying) {
			target.pause();
			target._isPlaying = false;
		}
	};

	// Observe all autoplay videos in a given container, skipping any already registered.
	function observeVideos(container) {
		const videos = [
			...container.querySelectorAll(
				'[data-video="autoplay"]:not([data-autoplay-observed])'
			),
		];
		if (!videos.length) return;
		videos.forEach(v => {
			v.setAttribute('data-autoplay-observed', '');
			applyPoster(v);
		});
		inview.observe(videos, { enter, exit });
	}

	observeVideos(node);

	// Re-run after any HTMX swap — detail.target is the element that received new content.
	const onSwap = ({ detail }) => {
		if (detail?.target) observeVideos(detail.target);
	};
	document.addEventListener('htmx:afterSwap', ({ target }) => {
		if (target.id !== 'results') return;
		onSwap({ detail: { target } });
	});

	// Swap src on all responsive videos when the lg breakpoint is crossed. The crop
	// switches on its own — `u-crop` carries the same 1024px media query.
	function onBreakpointChange() {
		node
			.querySelectorAll('[data-video="autoplay"][data-src-mobile]')
			.forEach(video => {
				const newSrc = resolveVideoSrc(video);
				if (video.src === newSrc) return;

				const wasPlaying = video._isPlaying;
				video.pause();
				video._isPlaying = false;
				video.removeAttribute('data-playing');

				applyPoster(video);

				video.src = newSrc;
				video.load();

				if (wasPlaying) initiatePlay(video);
			});
	}

	mql.addEventListener('change', onBreakpointChange);

	return () => {
		document.removeEventListener('htmx:afterSwap', onSwap);
		mql.removeEventListener('change', onBreakpointChange);
	};
}

export default withPlugins(inview)(AutoplayVideos);
